import { merge, isObject, includes, isString, forEach, has, clone, isArray, capitalize } from 'lodash';
import { I18n } from 'react-i18nify';
import Logger from '@sm360/logger-sentry';
import ConfigsService from 'commons-core-services/services/ConfigsService/ConfigsService.js';
import EN_WORDS_EXCEPTION from 'commons-core-services/constants/ENWordsException.js';

/* eslint-disable no-underscore-dangle */

function normalizeLocaleSeparators(locale, separators, defaultSep) {
    return separators.reduce((reducer, separator) => {
        return separator !== defaultSep && includes(reducer, separator)
            ? reducer.replace(new RegExp(separator, 'g'), defaultSep)
            : reducer;
    }, locale);
}

function formatCurrencyManually(value, locale) {
    return locale && includes(locale.toLowerCase(), 'fr') ? `${value}$` : `$${value}`;
}

class LocaleService {
    constructor() {
        this.logger = new Logger('LocaleService', ConfigsService.getLoggerContext());

        this.__currentTranslations = {};
        this.__locale = null;
    }

    /**
     * Helper function to localize a `value`, given a set of `options`
     *
     * If Intl API is not available in the browser, fallback on a manual localization
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
     * @see https://caniuse.com/#feat=internationalization
     *
     * @param {Number|Date} value
     * @param {Object} options - @see https://github.com/JSxMachina/react-i18nify#lvalue-options
     * @returns {String} localized value
     */
    l(value, options = {}) {
        try {
            const formattedValue = I18n.l(value, options);

            if (options.style === 'currency') {
                // We remove CA otherwise in english it would display CA$1,000.00
                return formattedValue.replace('CA', '');
            }

            return formattedValue;
        } catch (e) {
            // *** applies formatting by hand beacause ios9 doesn't support Intl*** //
            const transformValue = value.toFixed(options.maximumFractionDigits);
            return options.style === 'currency'
                ? formatCurrencyManually(transformValue, this.__locale)
                : transformValue;
        }
    }

    t(value, parameters) {
        try {
            return I18n.t(value, parameters);
        } catch (e) {
            e.message = `LocaleService - Error while translating (${value})\n${e.message}`;
            throw e;
        }
    }

    /**
     * Helper function to capitalize a `value`, according to the current locale
     * for now, the `en` locale will get each words capitalized while all other
     * locale will get only the first word capitalized.
     *
     * @param {String} value
     * @returns {String} capitalized value
     */
    capitalizeTitle(value) {
        if (!isString(value)) {
            throw new TypeError('LocaleService::capitalize - [value] must be a string');
        }
        if (this.getSimpleLocale().toLowerCase() === 'en') {
            const splitString = value.toLowerCase().split(' ');
            let wordCapitalized = '';
            const titleCaseResult = [];
            splitString.map((word, i) => {
                if (!EN_WORDS_EXCEPTION.includes(word)) {
                    wordCapitalized = capitalize(word);
                    titleCaseResult.push(wordCapitalized);
                } else if (i === 0 || splitString.length === i + 1) {
                    wordCapitalized = capitalize(word);
                    titleCaseResult.push(wordCapitalized);
                } else {
                    titleCaseResult.push(word);
                }
                return titleCaseResult;
            });

            return titleCaseResult.join(' ');
        }
        return capitalize(value);
    }

    formatLocale(locale) {
        if (!isString(locale)) {
            throw new TypeError('LocaleService::formatLocale - [locale] must be a string');
        }

        const defaultSep = '-';
        const separators = ['-', '_'];
        let _locale = '';
        if (locale.length) {
            _locale = normalizeLocaleSeparators(locale, separators, defaultSep);
        }

        const temp = _locale.split(defaultSep);
        const lang = temp.shift();
        const countryCode = temp.join('') || '';

        return lang && countryCode
            ? `${lang.toLowerCase()}${defaultSep}${countryCode.toUpperCase()}`
            : lang.toLowerCase();
    }

    setLocale(locale) {
        if (!isString(locale)) {
            throw new TypeError('LocaleService::setLocale - [locale] must be a string');
        }

        this.__locale = this.formatLocale(locale);
        I18n.setLocale(this.__locale);
    }

    getLocale() {
        return this.__locale;
    }

    getSimpleLocale() {
        return this.__locale && this.__locale.includes('-') ? this.__locale.split('-')[0] : this.__locale;
    }

    loadTranslations(translations) {
        I18n.setTranslations(this.mergeTranslations(translations));
    }

    mergeTranslations(translations) {
        const i18nTranslations = clone(this.__currentTranslations);

        if (!isObject(translations) || isArray(translations)) {
            return i18nTranslations;
        }

        forEach(translations, (values, key) => {
            if (has(i18nTranslations, key)) {
                i18nTranslations[key] = merge(i18nTranslations[key], values);
            } else {
                i18nTranslations[key] = values;
            }
        });

        this.__currentTranslations = i18nTranslations;

        return i18nTranslations;
    }
}

export default new LocaleService();



// WEBPACK FOOTER //
// ./src/services/LocaleService/LocaleService.js